import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Save, Trash2 } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { useAnthropicAuth } from "@/api/queries/anthropicAuth";
import { useSetAnthropicAuth, useClearAnthropicAuth } from "@/api/mutations/anthropicAuth";

// #227: Anthropic-Zugang verwalten (admin-only). EINE Komponente für beide Scopes:
//   - ohne agentId → Plattform-Default (gilt für alle Agents ohne eigenen Zugang)
//   - mit agentId   → per-Agent-Override (gewinnt über den Plattform-Default)
// Das Secret ist write-only: Stand zeigt nur isSet/Methode/Datum, nie den Wert.

const INPUT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50";

const METHODS = [
  { value: "apiKeyEnv", label: "API-Key (ANTHROPIC_API_KEY)", secretLabel: "API-Key", placeholder: "sk-ant-…" },
  { value: "oauthTokenEnv", label: "Subscription-Token (CLAUDE_CODE_OAUTH_TOKEN)", secretLabel: "OAuth-Token", placeholder: "sk-ant-oat…" },
  { value: "apiKeyHelper", label: "apiKeyHelper (Pull, rotierbar)", secretLabel: "Secret (vom Helper geliefert)", placeholder: "sk-ant-…" },
] as const;

export function AnthropicAuthSection({ agentId }: { agentId?: string }) {
  const isPlatform = !agentId;
  const authQuery = useAnthropicAuth(agentId);
  const setAuth = useSetAnthropicAuth(agentId);
  const clearAuth = useClearAnthropicAuth(agentId);
  const info = authQuery.data;

  const [method, setMethod] = useState<string>("apiKeyEnv");
  const [secret, setSecret] = useState("");
  const isPending = setAuth.isPending || clearAuth.isPending;
  const methodMeta = METHODS.find((m) => m.value === method) ?? METHODS[0];

  async function save() {
    if (!secret.trim()) {
      toast.error("Secret darf nicht leer sein");
      return;
    }
    try {
      await setAuth.mutateAsync({ method, secret });
      setSecret("");
      toast.success("Anthropic-Zugang gespeichert");
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  async function clear() {
    try {
      await clearAuth.mutateAsync();
      toast.success(isPlatform ? "Plattform-Default entfernt" : "Override entfernt");
    } catch (err) {
      toast.error(`Entfernen fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  const activeLabel = info?.method
    ? METHODS.find((m) => m.value === info.method)?.label ?? info.method
    : null;

  return (
    <SectionCard
      icon={<KeyRound className="h-[18px] w-[18px]" />}
      title={isPlatform ? "Anthropic-Zugang (Plattform-Default)" : "Anthropic-Zugang (dieser Agent)"}
      description={
        isPlatform
          ? "Gilt für alle Agents ohne eigenen Zugang. Das Secret ist write-only."
          : "Überschreibt den Plattform-Default nur für diesen Agent. Das Secret ist write-only."
      }
    >
      <div className="space-y-4">
        {/* Status */}
        <div className="rounded-md border border-border bg-surface2 px-3 py-2 text-[12.5px]" data-testid="anthropic-auth-status">
          {info?.isSet ? (
            <span className="text-text2">
              Aktiv: <span className="font-semibold text-text">{activeLabel}</span>
              {info.updatedAt && (
                <span className="text-text3"> · zuletzt {new Date(info.updatedAt).toLocaleString()}</span>
              )}
            </span>
          ) : (
            <span className="text-text3">
              {isPlatform
                ? "Kein Plattform-Default — Agents ohne eigenen Zugang haben keine Anthropic-Auth."
                : "Kein eigener Zugang — der Plattform-Default greift."}
            </span>
          )}
        </div>

        {/* Methode */}
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-text2">Methode</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            disabled={isPending}
            className={INPUT_CLASS}
            data-testid="anthropic-auth-method"
          >
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Secret (write-only) */}
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-text2">{methodMeta.secretLabel}</label>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            disabled={isPending}
            autoComplete="off"
            placeholder={info?.isSet ? "•••••• (neuen Wert eingeben zum Ersetzen)" : methodMeta.placeholder}
            className={INPUT_CLASS}
            data-testid="anthropic-auth-secret"
          />
          <span className="mt-1 block text-[11px] text-text3">
            Write-only — der gespeicherte Wert wird nie zurückgegeben.
          </span>
        </div>

        {/* Aktionen */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={clear}
            disabled={isPending || !info?.isSet}
            data-testid="anthropic-auth-clear"
          >
            <Trash2 className="h-4 w-4" />
            {isPlatform ? "Default entfernen" : "Override entfernen"}
          </Button>
          <Button size="sm" onClick={save} disabled={isPending || !secret.trim()} data-testid="anthropic-auth-save">
            <Save className="h-4 w-4" />
            Speichern
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
